<template>
  <el-col
    :span="24"
    id="assets"
    v-loading.body="(receiving || changingPage) && !navigating"
    element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" id="assets-header">
      <HeaderInfoText :colspan="9" title="ASSETS" :meta="meta" :selection="selection"></HeaderInfoText>
      <el-col :span="6" class="h-padded right-aligned-col">
        <div v-if="deleting" class="delete-notif">Deleting...</div>
        <el-select
          :disabled="!(selection && selection.length)"
          v-model="batchUpdateSelection"
          placeholder="With selected..."
          style="padding: 0 6px;"
          @change="handleBatchUpdateSelection">
          <el-option
            v-for="option in batchUpdateOptions"
            :key="option.id"
            :label="option.text"
            :value="option.id">
          </el-option>
        </el-select>
        <el-button type="primary" plain icon="plus" @click="createNewModel"></el-button>
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <el-table
          v-if="meta.count && !changingPage"
          id="assets-table"
          ref="assetsTable"
          :data="assets"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight"
          @select="handleSelectionChange"
          @select-all="handleSelectionChange">
          <el-table-column type="selection" width="55" align="center"></el-table-column>
          <el-table-column prop="name" label="Name" align="left" fit></el-table-column>
          <el-table-column prop="value" label="Value" align="left" fit sortable></el-table-column>
          <el-table-column prop="inventory_id" label="Inventory ID" align="left" fit sortable></el-table-column>
          <el-table-column prop="sale_date" label="Sale Date" align="left" fit sortable></el-table-column>
        </el-table>
      </el-col>
    </el-row>
    <el-dialog
      :title="editMode.active ? `Edit asset` : 'Add a new asset'"
      size="small"
      :visible.sync="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        :modalOpen="dialogFormVisible"
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'">
      </NewEntityForm>
      <span slot="footer" class="dialog-footer">
      <el-button :disabled="saving || !!updating" @click="dialogFormVisible = false">Cancel</el-button>
      <el-button :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
    </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { payload } from '@/components/RouteComponents/Assets/actionPayload'
  import store from '@/store/store'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import {
    handleBatchUpdateSelection,
    handleSelectionChange,
    restoreSelection,
    createNewModel,
    changePage,
    submitFormBasic
  } from '@/components/RouteComponents/commonFunctions'

  export default {
    components: {
      NewEntityForm,
      HeaderInfoText
    },
    data () {
      return {
        tableHeight: 1000,
        batchUpdateOptions: [
          { id: 0, text: 'Delete selected' }
        ],
        batchUpdateSelection: null,
        selection: [],
        dialogFormVisible: false,
        fields: [                                                                               // List of fields that will be passed to the NewEntityForm component. Specifies fields and their input types
          {
            label: 'Name',
            name: 'name',
            dataType: 'string',
            fieldType: 'input',
            required: true,
            msg: 'Please specify a name'
          },
          {
            label: 'Value',
            name: 'value',
            dataType: 'string',
            fieldType: 'input'
          },
          {
            label: 'Inventory ID',
            name: 'inventory_id',
            dataType: 'string',
            fieldType: 'input'
          },
          {
            name: 'sale_date',
            label: 'Sale Date',
            dataType: 'string',
            fieldType: 'date',
            required: true,
            msg: 'Please specify a date'
          }
        ],
        formInitState: {
          name: '',
          value: '',
          inventory_id: '',
          sale_date: null
        },
        form: {
          name: '',
          value: '',
          inventory_id: '',
          sale_date: null
        },
        currentPage: 1,
        editMode: { active: false, modelId: null },
        changingPage: false
      }
    },
    methods: {
      /**
       * Handles what happens when the user clicks on one of the "With selected..." dropdown options
       */
      handleBatchUpdateSelection () {
        handleBatchUpdateSelection(this, payload)
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        handleSelectionChange(this, selection)
      },
      /**
       * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
       * due to a page or tab change
       */
      restoreSelection () {
        restoreSelection(this, 'assetsTable')
      },
      /**
       * Handles paginating between assets pages
       * @param e  Target page
       */
      changePage (e) {
        changePage({ self: this, page: e, payload, query: null, getDeps: false })
      },
      /**
       * Submits the form
       */
      submitForm () {
        submitFormBasic(this, payload)
      },
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        createNewModel(this)
      }
    },
    computed: {
      ...mapGetters({
        assets: 'getAssets',
        meta: 'getAssetMetadata',
        receiving: 'getAssetFetchStatus',
        saving: 'getAssetSaveStatus',
        deleting: 'getAssetDeleteStatus'
      })
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    beforeRouteEnter (to, from, next) {
      store.dispatch('clearLocalState', { clearMutation: payload.clearMutation, clearCache: true })
        .then(() => store.dispatch('fetch', payload))
        .then(entities => store.dispatch('findDependencies', entities))
        .then(promises => Promise.all(promises))
        .then(() => next())
        .catch(e => console.log(e))
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('assets').offsetHeight
          const headerHeight = document.getElementById('assets-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    }
  }
</script>

<style scoped>
  #assets-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Assets/Assets.vue